import { SubstitutionAvailability, type ItemScoreHistoryEntry } from '@evaltick/shared';
import { scoreToBandIndex } from '@/utils';
import type { InterviewTemplateItem, TemplateItemHistory } from '@/services/api';

// Bottom two of the five score bands (see utils/scoreBands.ts) — the same
// cutoff EvalChart uses to color a question's bar red.
const RECOMMENDED_BAND_THRESHOLD = 1;

// Warning for a Different-question pick that can't reach an unseen question.
// Nothing for `Unseen` — that's what the control already promises.
export const EXHAUSTED_POOL_NOTES: Partial<Record<SubstitutionAvailability, string>> = {
  [SubstitutionAvailability.RepeatOnly]:
    "You've answered every question we have for this competency and type. You'll still get a different one from your last attempt — just one you've answered before.",
  [SubstitutionAvailability.None]:
    "There's no other question for this competency and type yet, so you'll be asked the same one again.",
};

/** The candidate's latest attempt at each question, keyed by template item id. */
export const lastAttemptByItemId = (
  entries: ItemScoreHistoryEntry[] | undefined
): Map<string, ItemScoreHistoryEntry> =>
  new Map((entries ?? []).map((entry) => [entry.interviewTemplateItemId, entry]));

/** Answered but low-scoring. Unscored (every dimension Not assessed) counts as low. */
export const isRecommendedRetry = (lastAttempt: ItemScoreHistoryEntry | undefined): boolean =>
  !!lastAttempt &&
  (lastAttempt.overallScore == null || scoreToBandIndex(lastAttempt.overallScore) <= RECOMMENDED_BAND_THRESHOLD);

/**
 * The setup wizard's starting selection: every question on a first attempt
 * (no history); otherwise the low scorers plus anything never answered.
 */
export const defaultQuestionSelection = (
  items: InterviewTemplateItem[],
  history: TemplateItemHistory | null
): string[] => {
  if (!history) return items.map((item) => item.id);
  const lastAttempts = lastAttemptByItemId(history.itemScoreHistory);
  return items
    .filter((item) => !lastAttempts.has(item.id) || isRecommendedRetry(lastAttempts.get(item.id)))
    .map((item) => item.id);
};
